/**
 * Tax Provisioning — simple NZ tax estimator.
 * Enter net profit + entity type; get gross tax owing at current NZ
 * rates plus weekly / fortnightly / monthly savings targets to fund
 * it by 31 March. Snapshots flow Draft → Approve → Publish to portal.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { Loader2, Plus, ShieldCheck, Send, CheckCircle2, Trash2, Mail } from "lucide-react";

const NZD = new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 });
const nz = (v) => (v == null ? "—" : NZD.format(v));

const STATUS_CLASSES = {
    fully_funded: "text-emerald-800 bg-emerald-50 border-emerald-200",
    to_save:      "text-orange-800 bg-orange-50 border-orange-200",
};
const STATUS_LABEL = { fully_funded: "Fully funded", to_save: "Save now" };

const TP_COLS = [
    { id: "client",       label: "Client · v",     align: "left",  sortKey: (r) => (r.client_name || "").toLowerCase() },
    { id: "entity",       label: "Entity",         align: "left",  sortKey: (r) => (r.calc?.entity_type || "") },
    { id: "total_income", label: "Total income",   align: "right", sortKey: (r) => Number(r.calc?.total_taxable_income ?? r.calc?.net_profit) || 0 },
    { id: "income_tax",   label: "Income tax",     align: "right", sortKey: (r) => Number(r.calc?.gross_tax) || 0 },
    { id: "acc",          label: "ACC levy",       align: "right", sortKey: (r) => Number(r.calc?.acc_levy?.levy) || 0 },
    { id: "tax_paid",     label: "Tax paid",       align: "right", sortKey: (r) => Number(r.calc?.tax_already_paid) || 0 },
    { id: "still",        label: "Still to save",  align: "right", sortKey: (r) => Number(r.calc?.net_exposure) || 0 },
    { id: "weekly",       label: "Save / week",    align: "right", sortKey: (r) => Number(r.calc?.recommended?.weekly) || 0 },
    { id: "calc_status",  label: "Status",         align: "left",  sortKey: (r) => r.calc?.status || "zzz_none" },
    { id: "snap_status",  label: "Snapshot",       align: "left",  sortKey: (r) => r.status || "zzz_none" },
];
const TP_SORT_STORAGE_KEY = "clientops.tp.sort.v1";

function loadTpSort() {
    try {
        const raw = localStorage.getItem(TP_SORT_STORAGE_KEY);
        if (!raw) return { col: null, dir: null };
        const parsed = JSON.parse(raw);
        if (!parsed || !parsed.col) return { col: null, dir: null };
        return { col: parsed.col, dir: parsed.dir || "asc" };
    } catch { return { col: null, dir: null }; }
}
function nextTpSort(current, colId) {
    if (current.col !== colId) return { col: colId, dir: "asc" };
    if (current.dir === "asc")  return { col: colId, dir: "desc" };
    return { col: null, dir: null };
}
function sortTpRows(rows, sort) {
    if (!sort.col) return rows;
    const col = TP_COLS.find((c) => c.id === sort.col);
    if (!col) return rows;
    const mul = sort.dir === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
        const av = col.sortKey(a); const bv = col.sortKey(b);
        if (av === bv) return 0;
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * mul;
        return String(av).localeCompare(String(bv)) * mul;
    });
}

const ENTITY_OPTIONS = [
    { value: "individual",  label: "Individual / Sole trader" },
    { value: "sole_trader", label: "Sole trader" },
    { value: "company",     label: "Company (28% flat)" },
    { value: "trust",       label: "Trust (39% flat)" },
    { value: "partnership", label: "Partnership (passthrough)" },
];

export default function TaxProvisioning() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [selected, setSelected] = useState(new Set());
    const [bulkOpen, setBulkOpen] = useState(false);
    const [sort, setSort] = useState(() => loadTpSort());

    useEffect(() => {
        try { localStorage.setItem(TP_SORT_STORAGE_KEY, JSON.stringify(sort)); } catch { /* ignore */ }
    }, [sort]);

    const sortedRows = useMemo(() => sortTpRows(rows, sort), [rows, sort]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/tax-provisioning/snapshots");
            setRows(data.rows || []);
            setSelected(new Set());
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggle = (id) => setSelected((s) => {
        const n = new Set(s);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });
    const toggleAll = () => setSelected((s) => (s.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));

    return (
        <div className="space-y-4" data-testid="tax-provisioning-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-[22px] font-semibold text-[#0F1115]">Tax Provisioning</h1>
                    <p className="text-[13px] text-[#5E636E]">Simple NZ tax estimate — combine business profit, salary and other income to see how much to save.</p>
                </div>
                <div className="flex items-center gap-2">
                    {isAdmin && selected.size > 0 && (
                        <Button onClick={() => setBulkOpen(true)} variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" data-testid="tp-bulk-delete">
                            <Trash2 className="h-4 w-4 mr-1" /> Delete {selected.size}
                        </Button>
                    )}
                    <Button onClick={() => setCreateOpen(true)} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tp-new">
                        <Plus className="h-4 w-4 mr-1" /> New estimate
                    </Button>
                </div>
            </div>

            <Card className="p-0 overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center text-[13px] text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center text-[13px] text-[#5E636E]" data-testid="tp-empty">
                        No estimates yet. Click "New estimate" to create one.
                    </div>
                ) : (
                    <table className="w-full text-[13px]" data-testid="tp-table">
                        <thead className="bg-[#FAFAF7] text-[11.5px] uppercase tracking-wider text-[#5E636E]">
                            <tr>
                                <th className="w-8 px-3 py-2">
                                    <Checkbox checked={selected.size === rows.length && rows.length > 0} onCheckedChange={toggleAll} data-testid="tp-select-all" />
                                </th>
                                {TP_COLS.map((c) => {
                                    const active = sort.col === c.id;
                                    const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "";
                                    return (
                                        <th
                                            key={c.id}
                                            className={`px-3 py-2 font-medium ${c.align === "right" ? "text-right" : "text-left"} cursor-pointer select-none hover:text-[#0F1115]`}
                                            onClick={() => setSort((s) => nextTpSort(s, c.id))}
                                            data-testid={`tp-th-${c.id}`}
                                            title="Click to sort"
                                        >
                                            <span className="inline-flex items-center gap-1">
                                                {c.label}
                                                {arrow && <span className="text-[#0F1115]">{arrow}</span>}
                                            </span>
                                        </th>
                                    );
                                })}
                                <th className="text-right px-3 py-2 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((r) => {
                                const c = r.calc || {};
                                const acc = c.acc_levy || {};
                                const totalIncome = c.total_taxable_income ?? c.net_profit;
                                return (
                                    <tr key={r.id} className="border-t border-[#EEEDE7] hover:bg-[#FAFAF7]" data-testid={`tp-row-${r.id}`}>
                                        <td className="px-3 py-2">
                                            <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`tp-select-${r.id}`} />
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="font-medium text-[#0F1115]">{r.client_name}</div>
                                            <div className="text-[11px] text-[#5E636E]">v{r.version_number}</div>
                                        </td>
                                        <td className="px-3 py-2 capitalize">{(c.entity_type || "—").replace("_", " ")}</td>
                                        <td className="px-3 py-2 text-right">
                                            <div className="tabular-nums">{nz(totalIncome)}</div>
                                            {(c.salary_wages || c.other_income) && (
                                                <div className="text-[10.5px] text-[#5E636E]">{sourcesSummary(c)}</div>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-right tabular-nums">{nz(c.gross_tax)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums text-[#5E636E]">{acc.applies ? nz(acc.levy) : "—"}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{nz(c.tax_already_paid)}</td>
                                        <td className={`px-3 py-2 text-right tabular-nums ${c.net_exposure > 0 ? "text-red-700 font-semibold" : "text-[#5E636E]"}`}>{nz(c.net_exposure)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{nz(c.recommended?.weekly)}</td>
                                        <td className="px-3 py-2"><span className={`rounded-full border px-2 py-0.5 text-[11px] ${STATUS_CLASSES[c.status] || "border-[#E4E4E7]"}`}>{STATUS_LABEL[c.status] || "—"}</span></td>
                                        <td className="px-3 py-2 text-[11px] text-[#5E636E] capitalize">{r.status}</td>
                                        <td className="px-3 py-2 text-right">
                                            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setOpenId(r.id)} data-testid={`tp-open-${r.id}`}>Review</Button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </Card>

            {createOpen && <CreateSnapshotDialog onClose={() => setCreateOpen(false)} onSaved={(id) => { setCreateOpen(false); load(); setOpenId(id); }} />}
            {openId && <ReviewDialog snapshotId={openId} onClose={() => setOpenId(null)} onChanged={load} isAdmin={isAdmin} />}
            {bulkOpen && <BulkDeleteDialog ids={Array.from(selected)} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); load(); }} />}
        </div>
    );
}

function CreateSnapshotDialog({ onClose, onSaved }) {
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState({
        client_id: "",
        entity_type: "individual",
        business_profit: "",
        salary_wages: "",
        paye_withheld: "",
        other_income: "",
        other_income_label: "Other income",
        other_withheld: "",
        tax_already_paid: "",
        include_acc_levy: true,
        notes: "",
    });
    const [busy, setBusy] = useState(false);
    const [showSalary, setShowSalary] = useState(false);
    const [showOther, setShowOther] = useState(false);
    useEffect(() => { api.get("/clients/").then(({ data }) => setClients(data)).catch(() => {}); }, []);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // When client changes, prefill entity_type from that client.
    useEffect(() => {
        if (!form.client_id) return;
        const c = clients.find((x) => x.id === form.client_id);
        if (c?.entity_type) set("entity_type", c.entity_type);
    }, [form.client_id, clients]);

    // ACC only applies to individual-type entities.
    const accApplicable = ["individual", "sole_trader", "partnership"].includes(form.entity_type);
    useEffect(() => {
        if (!accApplicable && form.include_acc_levy) set("include_acc_levy", false);
        if (accApplicable && form.include_acc_levy !== true) set("include_acc_levy", true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.entity_type]);

    async function submit() {
        if (!form.client_id) { toast.error("Pick a client"); return; }
        if (form.business_profit === "" || isNaN(Number(form.business_profit))) { toast.error("Enter business profit"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/tax-provisioning/snapshots", {
                client_id: form.client_id,
                entity_type: form.entity_type,
                business_profit: Number(form.business_profit),
                salary_wages: Number(form.salary_wages || 0),
                paye_withheld: Number(form.paye_withheld || 0),
                other_income: Number(form.other_income || 0),
                other_income_label: form.other_income_label || "Other income",
                other_withheld: Number(form.other_withheld || 0),
                tax_already_paid: Number(form.tax_already_paid || 0),
                include_acc_levy: accApplicable ? !!form.include_acc_levy : false,
                notes: form.notes || null,
            });
            toast.success("Estimate created");
            onSaved(data.id);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="tp-create-dialog">
                <DialogHeader>
                    <DialogTitle>New tax estimate</DialogTitle>
                    <DialogDescription>Enter income and we'll estimate tax progressively using current NZ rates.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Client</Label>
                        <Select value={form.client_id} onValueChange={(v) => set("client_id", v)}>
                            <SelectTrigger data-testid="tp-client"><SelectValue placeholder="Pick a client…" /></SelectTrigger>
                            <SelectContent>{clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Entity type</Label>
                        <Select value={form.entity_type} onValueChange={(v) => set("entity_type", v)}>
                            <SelectTrigger data-testid="tp-entity"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {ENTITY_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Business profit ($)</Label>
                        <Input type="number" step="0.01" value={form.business_profit} onChange={(e) => set("business_profit", e.target.value)} placeholder="e.g. 10000" data-testid="tp-business-profit" autoFocus />
                    </div>

                    {!showSalary && (
                        <button type="button" onClick={() => setShowSalary(true)} className="text-[12.5px] text-[#0F1115] underline decoration-dotted" data-testid="tp-add-salary">
                            + Add salary &amp; wages
                        </button>
                    )}
                    {showSalary && (
                        <div className="rounded-md border border-[#EEEDE7] p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <Label className="mb-0">Salary &amp; wages</Label>
                                <button type="button" className="text-[11px] text-[#9CA0A8]" onClick={() => { setShowSalary(false); set("salary_wages", ""); set("paye_withheld", ""); }}>Remove</button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-[11px] text-[#5E636E]">Gross amount ($)</Label>
                                    <Input type="number" step="0.01" value={form.salary_wages} onChange={(e) => set("salary_wages", e.target.value)} placeholder="0" data-testid="tp-salary" />
                                </div>
                                <div>
                                    <Label className="text-[11px] text-[#5E636E]">PAYE withheld ($)</Label>
                                    <Input type="number" step="0.01" value={form.paye_withheld} onChange={(e) => set("paye_withheld", e.target.value)} placeholder="0" data-testid="tp-paye" />
                                </div>
                            </div>
                            <p className="text-[11px] text-[#5E636E]">PAYE withheld is added to "tax already paid".</p>
                        </div>
                    )}

                    {!showOther && (
                        <button type="button" onClick={() => setShowOther(true)} className="text-[12.5px] text-[#0F1115] underline decoration-dotted block" data-testid="tp-add-other">
                            + Add other income
                        </button>
                    )}
                    {showOther && (
                        <div className="rounded-md border border-[#EEEDE7] p-3 space-y-2">
                            <div className="flex items-center justify-between">
                                <Label className="mb-0">Other income</Label>
                                <button type="button" className="text-[11px] text-[#9CA0A8]" onClick={() => { setShowOther(false); set("other_income", ""); set("other_withheld", ""); set("other_income_label", "Other income"); }}>Remove</button>
                            </div>
                            <div>
                                <Label className="text-[11px] text-[#5E636E]">Label</Label>
                                <Input value={form.other_income_label} onChange={(e) => set("other_income_label", e.target.value)} placeholder="e.g. Rental, Interest, Dividends" data-testid="tp-other-label" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <Label className="text-[11px] text-[#5E636E]">Amount ($)</Label>
                                    <Input type="number" step="0.01" value={form.other_income} onChange={(e) => set("other_income", e.target.value)} placeholder="0" data-testid="tp-other-amount" />
                                </div>
                                <div>
                                    <Label className="text-[11px] text-[#5E636E]">Tax withheld ($) <span className="text-[10px] text-[#9CA0A8]">optional</span></Label>
                                    <Input type="number" step="0.01" value={form.other_withheld} onChange={(e) => set("other_withheld", e.target.value)} placeholder="0" data-testid="tp-other-withheld" />
                                </div>
                            </div>
                        </div>
                    )}

                    <div>
                        <Label>Other tax credits ($) <span className="text-[11px] text-[#9CA0A8]">optional — e.g. provisional tax paid</span></Label>
                        <Input type="number" step="0.01" value={form.tax_already_paid} onChange={(e) => set("tax_already_paid", e.target.value)} placeholder="0" data-testid="tp-tax-paid" />
                    </div>
                    <div className="flex items-start gap-2 rounded-md border border-[#EEEDE7] bg-[#FAFAF7] p-2.5">
                        <Checkbox id="tp-acc" checked={form.include_acc_levy && accApplicable} disabled={!accApplicable}
                                   onCheckedChange={(v) => set("include_acc_levy", !!v)} data-testid="tp-include-acc" />
                        <label htmlFor="tp-acc" className={`text-[12.5px] leading-snug ${accApplicable ? "text-[#0F1115]" : "text-[#9CA0A8]"}`}>
                            <b>Include ACC earner levy</b> (1.75% up to $156,641)
                            <div className="text-[11px] text-[#5E636E]">
                                {accApplicable
                                    ? "Applied to business profit + salary/wages combined (subject to cap). ACC estimate only — actual invoice may differ."
                                    : "Not applicable to companies or trusts."}
                            </div>
                        </label>
                    </div>
                    <div>
                        <Label>Notes <span className="text-[11px] text-[#9CA0A8]">optional</span></Label>
                        <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tp-create-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create estimate"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ReviewDialog({ snapshotId, onClose, onChanged, isAdmin }) {
    const [doc, setDoc] = useState(null);
    const [busy, setBusy] = useState(false);
    const [emailOpen, setEmailOpen] = useState(false);
    const load = useCallback(async () => {
        const { data } = await api.get(`/tax-provisioning/snapshots/${snapshotId}`);
        setDoc(data);
    }, [snapshotId]);
    useEffect(() => { load(); }, [load]);

    if (!doc) return null;
    const c = doc.calc || {};

    async function act(url, msg) {
        setBusy(true);
        try { await api.post(url); toast.success(msg); await load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function del() {
        if (!confirm("Delete this estimate?")) return;
        setBusy(true);
        try {
            await api.delete(`/tax-provisioning/snapshots/${snapshotId}`);
            toast.success("Deleted");
            onChanged?.();
            onClose();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="tp-review-dialog">
                <DialogHeader>
                    <DialogTitle>{doc.client_name} · v{doc.version_number} <span className="text-[12px] font-normal text-[#5E636E] capitalize">({doc.status})</span></DialogTitle>
                    <DialogDescription className="capitalize">
                        Entity: <b>{(c.entity_type || "").replace("_", " ")}</b> · As at {c.as_at}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-2 gap-2 text-[13px]">
                    <Cell label="Total taxable income" value={nz(c.total_taxable_income ?? c.net_profit)} sub={sourcesSummary(c)} />
                    <Cell label="Income tax" value={nz(c.gross_tax)} sub={c.method_note} />
                    <Cell
                        label={`ACC earner levy${c.acc_levy?.capped ? " · capped" : ""}`}
                        value={c.acc_levy?.applies ? nz(c.acc_levy.levy) : "n/a"}
                        sub={c.acc_levy?.applies ? `1.75% on ${nz(c.acc_levy.taxed_on)}` : "Not applicable to this entity"}
                    />
                    <Cell label="Tax already paid" value={nz(c.tax_already_paid)} />
                    <Cell label="Total to fund" value={nz(c.total_to_fund)} />
                    <Cell label="Still to save" value={nz(c.net_exposure)} highlight={c.net_exposure > 0} />
                </div>

                <div className={`rounded-md border px-3 py-2 text-[12.5px] ${STATUS_CLASSES[c.status] || ""}`} data-testid="tp-status">
                    {c.status === "fully_funded" ? (
                        <><b>Fully funded.</b> Tax already paid covers the estimated liability.</>
                    ) : (
                        <>
                            <b>Save now.</b> To have <b>{nz(c.net_exposure)}</b> set aside by <b>{c.fy_end}</b> (in {c.days_remaining} days):{" "}
                            <b>{nz(c.recommended?.weekly)}</b>/week · <b>{nz(c.recommended?.fortnightly)}</b>/fortnight · <b>{nz(c.recommended?.monthly)}</b>/month
                        </>
                    )}
                </div>

                {(c.income_sources || []).length > 0 && (
                    <div className="rounded-md border border-[#EEEDE7] p-3 text-[12.5px]" data-testid="tp-income-sources">
                        <div className="font-semibold text-[#0F1115] mb-1.5">Income breakdown</div>
                        <table className="w-full">
                            <thead>
                                <tr className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">
                                    <th className="text-left font-medium py-1">Source</th>
                                    <th className="text-right font-medium py-1">Amount</th>
                                    <th className="text-right font-medium py-1">Tax withheld</th>
                                </tr>
                            </thead>
                            <tbody>
                                {c.income_sources.map((s) => (
                                    <tr key={s.key} className="border-t border-[#EEEDE7]">
                                        <td className="py-1.5">{s.label}</td>
                                        <td className="py-1.5 text-right tabular-nums">{nz(s.amount)}</td>
                                        <td className="py-1.5 text-right tabular-nums text-[#5E636E]">{s.withheld ? nz(s.withheld) : "—"}</td>
                                    </tr>
                                ))}
                                <tr className="border-t border-[#EEEDE7]">
                                    <td className="py-1.5 font-semibold">Total taxable income</td>
                                    <td className="py-1.5 text-right font-semibold tabular-nums">{nz(c.total_taxable_income)}</td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="rounded-md border border-[#EEEDE7] p-3 text-[12.5px]">
                    <div className="font-semibold text-[#0F1115] mb-1">How was this calculated?</div>
                    <table className="w-full">
                        <tbody>
                            <Row label="Total taxable income" v={nz(c.total_taxable_income ?? c.net_profit)} />
                            <Row label={`× ${c.method_note}`} v="" muted />
                            <Row label="= Income tax" v={nz(c.gross_tax)} bold />
                            {c.acc_levy?.applies && (
                                <Row label={`+ ACC earner levy · 1.75% on ${nz(c.acc_levy.taxed_on)}${c.acc_levy.capped ? " (capped)" : ""}`} v={nz(c.acc_levy.levy)} />
                            )}
                            <Row label="= Total to fund" v={nz(c.total_to_fund)} bold />
                            <Row label="− Tax already paid" v={`(${nz(c.tax_already_paid)})`} />
                            <Row label="= Still to save" v={nz(c.net_exposure)} bold highlight={c.net_exposure > 0} />
                        </tbody>
                    </table>
                </div>

                <BracketBreakdown breakdown={c.bracket_breakdown} effectiveRate={c.effective_rate} grossTax={c.gross_tax} acc={c.acc_levy} />

                {c.acc_levy?.applies && (
                    <p className="text-[11px] text-[#5E636E] leading-relaxed">
                        <b>Note on ACC:</b> ACC earner levy shown is an estimate for savings planning only. Your actual ACC invoice may differ.
                    </p>
                )}

                {c.notes && <div className="rounded-md border border-[#EEEDE7] p-3 text-[12.5px] text-[#5E636E]"><b className="text-[#0F1115]">Notes:</b> {c.notes}</div>}

                <p className="text-[11px] text-[#5E636E] leading-relaxed">
                    This is an estimate based on the net profit entered and current New Zealand tax rates. Final tax obligations may differ after year-end adjustments. Not a final tax assessment.
                </p>

                <DialogFooter className="mt-2 gap-2">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
                    {isAdmin && (
                        <Button variant="outline" onClick={del} disabled={busy} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="tp-delete">
                            <Trash2 className="h-4 w-4 mr-1" /> Delete
                        </Button>
                    )}
                    {doc.status === "draft" && isAdmin && (
                        <Button onClick={() => act(`/tax-provisioning/snapshots/${snapshotId}/approve`, "Approved")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tp-approve">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldCheck className="h-4 w-4 mr-1" /> Approve</>}
                        </Button>
                    )}
                    {(doc.status === "approved" || doc.status === "published") && (
                        <Button variant="outline" onClick={() => setEmailOpen(true)} disabled={busy} data-testid="tp-send-email">
                            <Mail className="h-4 w-4 mr-1" /> Send by email
                        </Button>
                    )}
                    {doc.status === "approved" && isAdmin && (
                        <Button onClick={() => act(`/tax-provisioning/snapshots/${snapshotId}/publish`, "Published to portal")} disabled={busy} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tp-publish">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Publish to portal</>}
                        </Button>
                    )}
                    {doc.status === "published" && (
                        <span className="inline-flex items-center gap-1 text-[12px] text-emerald-800"><CheckCircle2 className="h-3.5 w-3.5" /> Live on client portal</span>
                    )}
                </DialogFooter>
            </DialogContent>
            {emailOpen && <SendEmailDialog snapshotId={snapshotId} onClose={() => setEmailOpen(false)} onSent={() => { setEmailOpen(false); load(); }} />}
        </Dialog>
    );
}

function SendEmailDialog({ snapshotId, onClose, onSent }) {
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [to, setTo] = useState("");
    const [ccFirm, setCcFirm] = useState(true);
    const [firmCopy, setFirmCopy] = useState("");
    const [subject, setSubject] = useState("");
    const [html, setHtml] = useState("");
    const [note, setNote] = useState("");
    const [intro, setIntro] = useState("");
    const bodyRef = useRef(null);

    // Load default preview.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.post(`/tax-provisioning/snapshots/${snapshotId}/preview-email`, {});
                setTo(data.default_to || "");
                setFirmCopy(data.firm_copy_email || "");
                setSubject(data.subject || "");
                setHtml(data.html || "");
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, [snapshotId]);

    // Push initial html into the editable div once.
    useEffect(() => {
        if (bodyRef.current && html && !bodyRef.current.dataset.seeded) {
            bodyRef.current.innerHTML = html;
            bodyRef.current.dataset.seeded = "1";
        }
    }, [html]);

    // Regenerate the preview whenever intro/note change (server-rendered).
    async function refresh() {
        setBusy(true);
        try {
            const { data } = await api.post(`/tax-provisioning/snapshots/${snapshotId}/preview-email`, {
                intro: intro || null, note: note || null,
            });
            setSubject(data.subject || subject);
            if (bodyRef.current) {
                bodyRef.current.innerHTML = data.html || "";
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    async function submit() {
        if (!to.trim()) { toast.error("Enter a recipient email"); return; }
        setBusy(true);
        try {
            await api.post(`/tax-provisioning/snapshots/${snapshotId}/send-email`, {
                to: to.trim(),
                cc_firm: ccFirm,
                subject: subject.trim() || null,
                html: bodyRef.current?.innerHTML || html,
                intro: intro || null,
                note: note || null,
            });
            toast.success("Email sent");
            onSent();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
            <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="tp-send-email-dialog">
                <DialogHeader>
                    <DialogTitle>Send tax estimate by email</DialogTitle>
                    <DialogDescription>
                        Preview and edit the message. The recipient sees the body exactly as shown below.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="p-8 text-center text-[13px] text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Building preview…</div>
                ) : (
                    <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>To</Label>
                                <Input value={to} onChange={(e) => setTo(e.target.value)} data-testid="tp-email-to" />
                            </div>
                            <div className="flex items-end gap-2 pb-1">
                                <Checkbox id="tp-cc-firm" checked={ccFirm} onCheckedChange={(v) => setCcFirm(!!v)} data-testid="tp-email-cc-firm" />
                                <label htmlFor="tp-cc-firm" className="text-[13px] text-[#0F1115]">
                                    CC firm ({firmCopy || "not configured"})
                                </label>
                            </div>
                        </div>
                        <div>
                            <Label>Subject</Label>
                            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="tp-email-subject" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Intro <span className="text-[11px] text-[#9CA0A8]">optional — plain text</span></Label>
                                <Textarea rows={3} value={intro} onChange={(e) => setIntro(e.target.value)} onBlur={refresh} placeholder="Leave blank for default greeting" data-testid="tp-email-intro" />
                            </div>
                            <div>
                                <Label>Accountant note <span className="text-[11px] text-[#9CA0A8]">optional</span></Label>
                                <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} onBlur={refresh} placeholder="Appears in a highlighted box below the numbers" data-testid="tp-email-note" />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <Label>Preview <span className="text-[11px] text-[#9CA0A8]">click to edit inline</span></Label>
                                <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={refresh} disabled={busy}>Reset preview</Button>
                            </div>
                            <div
                                ref={bodyRef}
                                contentEditable
                                suppressContentEditableWarning
                                className="rounded-md border border-[#EEEDE7] bg-white p-4 max-h-[420px] overflow-y-auto text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0F1115]/10"
                                data-testid="tp-email-preview"
                            />
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || loading} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tp-email-send">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4 mr-1" /> Send now</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkDeleteDialog({ ids, onClose, onDone }) {
    const [text, setText] = useState("");
    const [busy, setBusy] = useState(false);
    const need = "DELETE";
    const canDelete = text.trim() === need;

    async function submit() {
        if (!canDelete) return;
        setBusy(true);
        try {
            const { data } = await api.post("/tax-provisioning/snapshots/bulk-delete", { ids });
            toast.success(`Deleted ${data.deleted} estimate${data.deleted === 1 ? "" : "s"}`);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open onOpenChange={(v) => !busy && !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="tp-bulk-delete-dialog">
                <DialogHeader>
                    <DialogTitle>Delete {ids.length} estimate{ids.length === 1 ? "" : "s"}?</DialogTitle>
                    <DialogDescription>
                        This permanently removes the selected snapshots (including any published ones). Type <b>{need}</b> to confirm.
                    </DialogDescription>
                </DialogHeader>
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={need} data-testid="tp-bulk-delete-confirm" />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={!canDelete || busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="tp-bulk-delete-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function sourcesSummary(c) {
    const parts = [];
    if (c.business_profit ?? c.net_profit) parts.push(`Business ${(new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 })).format(c.business_profit ?? c.net_profit)}`);
    if (c.salary_wages) parts.push(`Salary ${(new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 })).format(c.salary_wages)}`);
    if (c.other_income) parts.push(`${c.other_income_label || "Other"} ${(new Intl.NumberFormat("en-NZ", { style: "currency", currency: "NZD", maximumFractionDigits: 0 })).format(c.other_income)}`);
    return parts.join(" · ");
}

function BracketBreakdown({ breakdown, effectiveRate, grossTax, acc }) {
    const hasAcc = acc && acc.applies;
    if ((!breakdown || !breakdown.length) && !hasAcc) return null;
    const pct = (r) => {
        const n = Math.round(Number(r) * 10000) / 100;
        const s = n.toString();
        return `${s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s}%`;
    };
    const total = Number(grossTax || 0) + (hasAcc ? Number(acc.levy || 0) : 0);
    return (
        <div className="rounded-md border border-[#EEEDE7] p-3 text-[12.5px]" data-testid="tp-bracket-breakdown">
            <div className="flex items-baseline justify-between mb-1.5">
                <div className="font-semibold text-[#0F1115]">Bracket breakdown</div>
                <div className="text-[11px] text-[#5E636E]">Effective rate {Number(effectiveRate || 0)}%</div>
            </div>
            <table className="w-full">
                <thead>
                    <tr className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">
                        <th className="text-left font-medium py-1">Band</th>
                        <th className="text-right font-medium py-1">Rate</th>
                        <th className="text-right font-medium py-1">Taxed on</th>
                        <th className="text-right font-medium py-1">Tax</th>
                    </tr>
                </thead>
                <tbody>
                    {(breakdown || []).map((b, i) => (
                        <tr key={i} className="border-t border-[#EEEDE7]">
                            <td className="py-1.5">{b.label}</td>
                            <td className="py-1.5 text-right">{pct(b.rate)}</td>
                            <td className="py-1.5 text-right tabular-nums">{nz(b.taxed_on)}</td>
                            <td className="py-1.5 text-right tabular-nums">{nz(b.tax)}</td>
                        </tr>
                    ))}
                    {grossTax != null && (breakdown || []).length > 0 && (
                        <tr className="border-t border-[#EEEDE7]">
                            <td className="py-1.5 text-[#5E636E]" colSpan={3}>Income tax subtotal</td>
                            <td className="py-1.5 text-right tabular-nums text-[#5E636E]">{nz(grossTax)}</td>
                        </tr>
                    )}
                    {hasAcc && (
                        <tr className="border-t border-[#EEEDE7]">
                            <td className="py-1.5">ACC earner levy{acc.capped ? " (capped)" : ""}</td>
                            <td className="py-1.5 text-right">{pct(acc.rate)}</td>
                            <td className="py-1.5 text-right tabular-nums">{nz(acc.taxed_on)}</td>
                            <td className="py-1.5 text-right tabular-nums">{nz(acc.levy)}</td>
                        </tr>
                    )}
                    <tr className="border-t border-[#EEEDE7]">
                        <td className="py-1.5 font-semibold" colSpan={3}>Total to fund</td>
                        <td className="py-1.5 text-right font-semibold tabular-nums">{nz(total)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function Cell({ label, value, sub, highlight }) {
    return (
        <div className={`rounded-md border p-2 ${highlight ? "border-red-200 bg-red-50" : "border-[#EEEDE7] bg-[#FAFAF7]"}`}>
            <div className="text-[10.5px] uppercase tracking-wider text-[#5E636E]">{label}</div>
            <div className="mt-1 text-[15px] font-semibold text-[#0F1115] tabular-nums">{value}</div>
            {sub && <div className="text-[11px] text-[#5E636E] mt-0.5 truncate">{sub}</div>}
        </div>
    );
}
function Row({ label, v, bold, muted, highlight }) {
    return (
        <tr className={muted ? "text-[#5E636E]" : ""}>
            <td className={`py-1 ${bold ? "font-semibold text-[#0F1115]" : ""}`}>{label}</td>
            <td className={`py-1 text-right tabular-nums ${bold ? "font-semibold" : ""} ${highlight ? "text-red-700" : ""}`}>{v}</td>
        </tr>
    );
}
